import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { sanitizeCkEditorHtml } from "configs";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import {
  formatMaxRatingLabel,
  isValidMaxRating,
} from "pages/AppraisalManagment/utils/appraisalProgressSummary";
import "../../../AppraisalProgress.css";

/** Match Max Rating field display in the form body. */
const formatMaxRatingDisplay = (row) => {
  const label = formatMaxRatingLabel(row?.rating);
  return label === "—" ? "" : label;
};

const OBTAINED_SCORE_INPUT_PATTERN = /^(?:\d+\.?\d{0,2}|\.\d{0,2})?$/;

const roundObtainedScore = (value) => {
  if (!Number.isFinite(value)) return null;
  return Math.round(value * 100) / 100;
};

const formatObtainedScoreValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toFixed(2);
};

const formatObtainedRatingDisplay = (row) => {
  const value = row?.obtained_score ?? row?.obtained_rating;
  if (value === null || value === undefined || value === "") return "—";
  const formatted = formatObtainedScoreValue(value);
  return formatted || "—";
};

const ACTUAL_RESULT_LABEL = "Comments";

const isIndividualObjectivesCategory = (categoryName = "") =>
  String(categoryName).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase() ===
  "individual objectives";

const resolveCommentsFieldLabel = (categoryName) =>
  isIndividualObjectivesCategory(categoryName) ? ACTUAL_RESULT_LABEL : "Comments";

const stripHtmlText = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasReadableDescription = (description) => stripHtmlText(description).length > 0;

const sanitizeDescriptionHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  return sanitizeCkEditorHtml(html)
    .replace(/<script\b[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe\b[\s\S]*?<\/iframe>/gi, "")
    .replace(/\s(on\w+)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
};

const SetupQuestionDescription = ({ description }) => {
  const cleaned = sanitizeDescriptionHtml(description);
  if (!hasReadableDescription(cleaned)) return null;

  return (
    <Row>
      <Col lg={12}>
        <div
          className="setup-question-description mb-3"
          dangerouslySetInnerHTML={{
            __html: cleaned,
          }}
        />
      </Col>
    </Row>
  );
};

/**
 * Sections (questions) grouped by category tab in appraisal progress.
 */
const SectionCategoryTab = ({
  categoryName,
  sections,
  onNext,
  hasNextTab = false,
  onCancel,
  onSectionChange,
  isReadOnly = false,
}) => {
  // Track validation per question key and keep it stable across renders
  const [scoreErrors, setScoreErrors] = useState({});
  const [scoreTouched, setScoreTouched] = useState({});
  const [collapsedQuestionKeys, setCollapsedQuestionKeys] = useState(() => new Set());
  const [editingScoreKey, setEditingScoreKey] = useState(null);
  const [editingScoreValue, setEditingScoreValue] = useState("");
  const commentsFieldLabel = resolveCommentsFieldLabel(categoryName);

  const getQuestionKey = (row, index) => row[F.setupPivotKey] || row.id || index;

  useEffect(() => {
    const nextKeys = new Set((sections || []).map((row, index) => (row[F.setupPivotKey] || row.id || index)));
    setScoreErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (nextKeys.has(key)) next[key] = prev[key];
      });
      return next;
    });
    setScoreTouched((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (nextKeys.has(key)) next[key] = prev[key];
      });
      return next;
    });
  }, [sections]);

  useEffect(() => {
    const validKeys = new Set((sections || []).map((row, index) => getQuestionKey(row, index)));
    setCollapsedQuestionKeys((prev) => {
      const next = new Set([...prev].filter((key) => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [sections]);

  if (!sections || sections.length === 0) {
    return (
      <div>
        <p className="text-muted">No section available for this category.</p>
        {(onNext || onCancel) && (
          <div>
            {onCancel && (
              <button type="button" className="c-b-btn" onClick={onCancel}>
                Cancel
              </button>
            )}
            {onNext && (
              <button type="button" className="n-u-btn ms-2" onClick={onNext} disabled={!hasNextTab}>
                Next
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  const validateObtainedScore = (row, parsedValue) => {
    const totalScore = Number(row?.rating);

    if (parsedValue === null) {
      return "Obtained Rating is required";
    }
    if (parsedValue <= 0) {
      return "Obtained Rating must be a positive numeric value";
    }
    if (Number.isFinite(totalScore) && !isValidMaxRating(totalScore)) {
      return "Max Rating must be percentage (0-100%)";
    }
    return "";
  };

  const getParsedObtainedScore = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleCommentsChange = (row, index, inputValue) => {
    const questionKey = row[F.setupPivotKey] || row.id || index;

    if (onSectionChange) {
      onSectionChange(questionKey, {
        ...row,
        comments: inputValue,
      });
    }
  };

  const handleDeservedScoreChange = (row, index, inputValue) => {
    // Parse the value - allow empty string for immediate UI feedback
    let parsedValue = null;
    if (inputValue !== "" && inputValue !== null && inputValue !== undefined) {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        parsedValue = roundObtainedScore(numValue);
      }
    }

    const questionKey = row[F.setupPivotKey] || row.id || index;
    const scoreError = validateObtainedScore(row, parsedValue);

    setScoreErrors((prev) => ({
      ...prev,
      [questionKey]: scoreError,
    }));
    setScoreTouched((prev) => ({
      ...prev,
      [questionKey]: true,
    }));
    
    // Notify parent component
    if (onSectionChange) {
      onSectionChange(questionKey, {
        ...row,
        obtained_score: parsedValue
      });
    }
  };

  return (
    <div>
      <div className="setup-content-div pb-3 pt-3">
        {sections.map((row, index) => {
          const questionKey = getQuestionKey(row, index);
          const isExpanded = !collapsedQuestionKeys.has(questionKey);
          const barLabel =
            (row.title != null && String(row.title).trim() !== "")
              ? String(row.title).trim()
              : `Question ${index + 1}`;
          const regionId = `appraisal-question-panel-${String(questionKey)}`;
          const headerId = `appraisal-question-header-${String(questionKey)}`;

          return (
          <div key={row.id || index} className="appraisal-question-row mb-4">
            <button
              type="button"
              className="appraisal-question-header-bar d-flex align-items-center justify-content-between w-100"
              id={headerId}
              aria-expanded={isExpanded}
              aria-controls={regionId}
              onClick={() => {
                setCollapsedQuestionKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(questionKey)) next.delete(questionKey);
                  else next.add(questionKey);
                  return next;
                });
              }}
            >
              <div className="appraisal-question-header-title-block d-flex align-items-center flex-grow-1 flex-wrap gap-2 pe-2 text-start">
                <span className="font-13 font-weight-s-bold heading-color">{barLabel}</span>
                {!isExpanded && (
                  <>
                    <span className="appraisal-question-header-meta">
                      <span className="appraisal-question-header-meta-label">Weightage</span>
                      {row.weightage != null && String(row.weightage).trim() !== ""
                        ? String(row.weightage).trim()
                        : "—"}
                    </span>
                    <span className="appraisal-question-header-meta">
                      <span className="appraisal-question-header-meta-label">Max Rating</span>
                      {formatMaxRatingDisplay(row) || "—"}
                    </span>
                  </>
                )}
                <span className="appraisal-question-header-meta">
                  <span className="appraisal-question-header-meta-label">Obtained Rating</span>
                  {formatObtainedRatingDisplay(row)}
                </span>
              </div>
              <span className="question-toggle-btn appraisal-question-toggle-btn flex-shrink-0" aria-hidden="true">
                {isExpanded ? "Collapse" : "Expand"}
              </span>
            </button>
            <div
              id={regionId}
              role="region"
              aria-labelledby={headerId}
              className={`appraisal-question-body ${isExpanded ? "expanded" : "collapsed"}`}
            >
              <div className="appraisal-question-body-inner">
            <Row className="mb-3">
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Weightage</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-field"
                    value={row.weightage || ""}
                    readOnly
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Max Rating</Form.Label>
                  <Form.Control
                    type="text"
                    className="input-field"
                    value={formatMaxRatingDisplay(row)}
                    readOnly
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Obtained Rating</Form.Label>
                  {(() => {
                    const liveError = validateObtainedScore(row, getParsedObtainedScore(row.obtained_score));
                    const error = scoreErrors[questionKey] || liveError;
                    return (
                      <>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    className={`input-field ${error ? "is-invalid" : ""}`}
                    value={
                      editingScoreKey === questionKey
                        ? editingScoreValue
                        : formatObtainedScoreValue(row.obtained_score)
                    }
                    onFocus={() => {
                      if (isReadOnly) return;
                      setEditingScoreKey(questionKey);
                      const raw = row.obtained_score;
                      setEditingScoreValue(
                        raw != null && raw !== "" && Number.isFinite(Number(raw))
                          ? String(raw)
                          : ""
                      );
                    }}
                    onBlur={(e) => {
                      if (isReadOnly) return;
                      const nextValue = e.target.value.trim();
                      if (nextValue === "") {
                        handleDeservedScoreChange(row, index, "");
                      } else {
                        const parsed = getParsedObtainedScore(nextValue);
                        if (parsed !== null) {
                          handleDeservedScoreChange(row, index, roundObtainedScore(parsed).toFixed(2));
                        }
                      }
                      if (editingScoreKey === questionKey) {
                        setEditingScoreKey(null);
                        setEditingScoreValue("");
                      }
                    }}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (nextValue !== "" && !OBTAINED_SCORE_INPUT_PATTERN.test(nextValue)) return;
                      setEditingScoreValue(nextValue);
                      handleDeservedScoreChange(row, index, nextValue);
                    }}
                    placeholder="Enter score (e.g. 89.00)"
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                  />
                  {error && (
                    <div className="text-danger mt-1 font-12">
                      {error}
                    </div>
                  )}
                      </>
                    );
                  })()}
                </Form.Group>
              </Col>
            </Row>

            <SetupQuestionDescription description={row.description} />

            <Row>
              <Col lg={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">{commentsFieldLabel}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    className="input-field appraisal-comments-textarea"
                    value={row.comments || ""}
                    onChange={(e) => handleCommentsChange(row, index, e.target.value)}
                    placeholder="Enter observations, actual results, or justification for the score (optional)"
                    disabled={isReadOnly}
                    readOnly={isReadOnly}
                  />
                </Form.Group>
              </Col>
            </Row>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      {(onNext || onCancel) && (
        <div className="p-0 pt-3">
          {onCancel && (
            <button type="button" className="c-b-btn" onClick={onCancel}>
              Cancel
            </button>
          )}
          {onNext && (
            <button
              type="button"
              className="n-u-btn ms-2"
              onClick={onNext}
              disabled={
                !hasNextTab ||
                !isReadOnly &&
                  (sections || []).some((row) => {
                    const parsed = getParsedObtainedScore(row?.obtained_score);
                    return Boolean(validateObtainedScore(row, parsed));
                  })
              }
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionCategoryTab;
